import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import Loading from './Loading';

const ProtectedRoute = ({ 
  children, 
  requireAuth = true, 
  requireEmailVerification = false, 
  redirectTo = '/login',
  fallback = null 
}) => {
  const { isAuthenticated, loading, initializing, isEmailVerified } = useAuth();
  const location = useLocation();

  // Show loading during auth initialization
  if (initializing || loading) {
    return <Loading />;
  }

  // Check authentication requirement
  if (requireAuth && !isAuthenticated) {
    // Store current location for redirect after login
    localStorage.setItem('redirectAfterLogin', location.pathname + location.search);
    return <Navigate to={redirectTo} replace />;
  }

  // Check email verification requirement
  if (requireAuth && requireEmailVerification && isAuthenticated && !isEmailVerified) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="mx-auto h-12 w-12 text-yellow-400">
              <svg fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
              Email Verification Required
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              Please verify your email address to access this page. Check your inbox for the verification email.
            </p>
            <div className="mt-6">
              <button
                onClick={() => window.location.href = '/account/verify-email'}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Verify Email
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // If we have a fallback and user doesn't meet requirements, show fallback
  if (fallback && requireAuth && !isAuthenticated) {
    return fallback;
  }

  // User meets all requirements, render children
  return children;
};

export default ProtectedRoute;